import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  Button,
  CircularProgress,
  Checkbox,
  Chip,
} from '@material-ui/core';
import { useStyles } from './styles';
import InsuranceLogoReSize from '../InsuranceLogo/InsuranceLogoReSize';
import QuotePreviewModal from '../QuotationModal/QuotationModal';
import { formatCurrency, validAndParseNumber } from 'utils/utils';
import { getInsuranceRate } from 'services/QuotationService';
import InsuranceLogoV2 from '../InsuranceLogo/InsuranceLogoV2';

const generateUniqueId = (quote: any): string => {
  return `${quote.insurerKey}-${quote.title}-${quote.licenseVehicle}`;
};

// Función para formatear valores monetarios (solo cotizaciones manuales)
const formatCurrencyValueForDisplay = (value: string | number, isManual: boolean = false): string => {
  if (!value) return "N/A";
  
  const stringValue = value.toString();
  
  // Si no es manual y ya tiene formato, lo devolvemos como está
  if (!isManual && (stringValue.includes('.') || stringValue.includes(','))) {
    return stringValue;
  }
  
  // Si es manual o no tiene formato, lo formateamos
  const numericValue = typeof value === 'string' ? 
    parseFloat(value.replace(/[^0-9]/g, '')) : value;
  if (isNaN(numericValue)) return "N/A";
  return numericValue.toLocaleString('es-CO');
};

// Función para mapear nombres de aseguradoras del API a las keys del componente InsuranceLogoV2
const mapInsurerKeyToLogoKey = (insurerKey: string): string => {
  if (!insurerKey) return '';
  
  // Normalizar el nombre de la aseguradora (convertir a mayúsculas)
  const normalized = insurerKey.toUpperCase().trim();
  
  const mappings: { [key: string]: string } = {
    'AXA': 'AXA',
    'BOLIVAR': 'BOLIVAR', 
    'EQUIDAD': 'EQUIDAD',
    'HDI': 'HDI', 
    'MAPFRE': 'MAPFRE',
    'SBS': 'SBS',
    'SURA': 'SURA',
    'LIBERTY': 'LIBERTY',
    'ZURICH': 'ZURICH',
    'PREVISORA': 'PREVISORA',
    'SOLIDARIA': 'SOLIDARIA',
    'SEGUROS_DEL_ESTADO': 'ESTADO',
    'ESTADO': 'ESTADO'
  };
  
  return mappings[normalized] || normalized;
};

const QuoteResultClient = ({
  filteredProductsQuotations,
}: {
  filteredProductsQuotations: any[];
}) => {
  const classes = useStyles();
  const [installmentPayments, setInstallmentPayments] = useState<{ [key: string]: string }>({});
  const [selectedInsurers, setSelectedInsurers] = useState<string[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [selectedQuotes, setSelectedQuotes] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [loading, setLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const filteredInsurers = [
    ...new Set(filteredProductsQuotations.map((quote) => quote.insurerKey)),
  ];

  const filteredSegments = [
    ...new Set(
      filteredProductsQuotations
        .filter(
          (quote) => selectedInsurers.length === 0 || selectedInsurers.includes(quote.insurerKey)
        )
        .map((quote) => quote.segment?.segment)
        .filter(Boolean)
    ),
  ];

  const fetchAndSetPaymentByInstallments = async (quoteId: string, valueToFinanced: number) => {
    const formatValueToFinanced = validAndParseNumber(valueToFinanced);
    const { insuranceRate } = await getInsuranceRate(formatValueToFinanced);

    if (insuranceRate) {
      const monthlyRate = insuranceRate[0]?.interest_rate;
      const numPeriods = 11;
      const priceGateway = insuranceRate[0]?.price_gateway;

      const payment =
        (monthlyRate * formatValueToFinanced) / (1 - Math.pow(1 + monthlyRate, -numPeriods));

      const formattedPayment = formatCurrency('es-CO', 'COP', 0, payment + priceGateway);

      setInstallmentPayments((prevState) => ({
        ...prevState,
        [quoteId]: formattedPayment,
      }));
    } else {
      const formattedPayment = formatCurrency('es-CO', 'COP', 0, formatValueToFinanced);
      setInstallmentPayments((prevState) => ({
        ...prevState,
        [quoteId]: formattedPayment,
      }));
    }
  };

  useEffect(() => {
    if (filteredProductsQuotations.length > 0) {
      filteredProductsQuotations.forEach((quote) => {
        const value = validAndParseNumber(quote.value);
        const uniqueId = generateUniqueId(quote);

        if (!installmentPayments[uniqueId]) {
          fetchAndSetPaymentByInstallments(uniqueId, value);
        }
      });
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [filteredProductsQuotations]);

  const handleSortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSortOrder(event.target.value);
  };

  const handleInsurerClick = (insurerKey: string) => {
    if (selectedInsurers.includes(insurerKey)) {
      setSelectedInsurers(selectedInsurers.filter((key) => key !== insurerKey));
    } else {
      setSelectedInsurers([...selectedInsurers, insurerKey]);
    }
  };

  const handleSegmentClick = (segment: string) => {
    if (selectedSegments.includes(segment)) {
      setSelectedSegments(selectedSegments.filter((seg) => seg !== segment));
    } else {
      setSelectedSegments([...selectedSegments, segment]);
    }
  };

  const handleQuoteSelect = (quote: any) => {
    const isSelected = selectedQuotes.some(
      (selected) => generateUniqueId(selected) === generateUniqueId(quote)
    );
    if (isSelected) {
      setSelectedQuotes(
        selectedQuotes.filter((selected) => generateUniqueId(selected) !== generateUniqueId(quote))
      );
    } else {
      setSelectedQuotes([...selectedQuotes, quote]);
    }
  };

  const handlePreviewClick = () => {
    setOpenModal(true);
  };

  const handleClearFilters = () => {
    setSelectedInsurers([]);
    setSelectedSegments([]);
    setSortOrder('desc');
  };

  const filteredQuotations = filteredProductsQuotations.filter((quote) => {
    const matchesInsurer =
      selectedInsurers.length === 0 || selectedInsurers.includes(quote.insurerKey);
    const matchesSegment =
      selectedSegments.length === 0 || selectedSegments.includes(quote.segment?.segment);
    return matchesInsurer && matchesSegment;
  });

  const sortedQuotations = [...filteredQuotations].sort((a, b) => {
    const valueA = validAndParseNumber(a.value);
    const valueB = validAndParseNumber(b.value);
    return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
  });

  return (
    <div>
      {loading ? (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          style={{ height: '70vh', width: '100vw' }}>
          <CircularProgress />
          <Typography variant="h6" style={{ marginTop: '20px' }}>
            {' '}
            Cargando vista cliente...
          </Typography>
        </Grid>
      ) : (
        <Grid container spacing={3} style={{ marginTop: 20 }}>
          <Grid item xs={12} sm={12} md={6} lg={8}>
            <Grid container spacing={1} justifyContent="flex-start">
              {sortedQuotations.map((quote, index) => {
                const uniqueId = generateUniqueId(quote);

                return (
                  <Grid item xs={12} sm={12} md={12} lg={12} key={uniqueId || index}>
                    <Card className={classes.root} variant="outlined">
                      <div className={classes.contentImage}>
                        <InsuranceLogoV2 insurance={quote.isManualQuote ? mapInsurerKeyToLogoKey(quote.insurerKey) : quote.insurerKey} />
                      </div>
                      <CardContent>
                        <Typography variant="body1" className={classes.titleColumnCard}>
                          Seguro todo riesgo auto
                        </Typography>
                        <Typography variant="body2">
                          Producto: {quote.title?.split('-')[1] || quote.title}
                        </Typography>
                        <Typography variant="body2">Placa: {quote.licenseVehicle}</Typography>
                        <Typography variant="body2">Nombre: {quote.firstName}</Typography>
                      </CardContent>
                      <CardContent>
                        <Typography variant="body1" className={classes.titleColumnCard}>
                          Pago
                        </Typography>
                        <Typography variant="body2">Contado: {formatCurrencyValueForDisplay(quote.value, quote.isManualQuote)}</Typography>
                      </CardContent>
                      <CardContent>
                        <Typography variant="body1" className={classes.titleColumnCard}>
                          Pago
                        </Typography>
                        <Typography variant="body2">
                          Mensuales: {installmentPayments[uniqueId] ? formatCurrencyValueForDisplay(installmentPayments[uniqueId], quote.isManualQuote) : 'Calculando...'}
                        </Typography>
                      </CardContent>
                      <CardContent className={classes.chipContainer}>
                        <Chip
                          label={quote?.segment?.segment || 'Sin Segmento'}
                          className={
                            quote?.segment?.segment === 'Premium'
                              ? classes.premiumChip
                              : quote?.segment?.segment === 'Basico'
                              ? classes.basicChip
                              : quote?.segment?.segment === 'Estandar'
                              ? classes.estandarChip
                              : classes.noClassChip
                          }
                        />
                      </CardContent>
                      <CardContent>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}>
                          <Checkbox
                            checked={selectedQuotes.some(
                              (selected) => generateUniqueId(selected) === uniqueId
                            )}
                            onChange={() => handleQuoteSelect(quote)}
                            disabled={
                              !quote?.segment?.segment || quote?.segment?.segment === 'Sin Segmento'
                            }
                            title={
                              !quote?.segment?.segment || quote?.segment?.segment === 'Sin Segmento'
                                ? 'No se puede seleccionar un producto sin segmento'
                                : ''
                            }
                          />
                          {(!quote?.segment?.segment ||
                            quote?.segment?.segment === 'Sin Segmento') && (
                            <Typography
                              variant="caption"
                              style={{
                                color: '#f44336',
                                textAlign: 'center',
                                marginTop: '4px',
                                fontSize: '0.75rem',
                                maxWidth: '200px',
                              }}>
                              No se puede seleccionar: Sin categoría ni coberturas asignadas.
                            </Typography>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.filterCard}>
              <CardContent>
                <Typography variant="h6" className={classes.titleFilter}>
                  Filtros
                </Typography>
                <Typography style={{ marginTop: '2px' }}>Aseguradoras</Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 10 }}>
                  {filteredInsurers.map((insurerKey) => (
                    <div
                      key={insurerKey}
                      style={{ margin: '5px' }}
                      onClick={() => handleInsurerClick(insurerKey)}>
                      <InsuranceLogoReSize insurance={insurerKey} width={60} heigth={50} />
                    </div>
                  ))}
                </div>
              </CardContent>

              <Divider style={{ margin: '10px 0' }} />

              <CardContent>
                <Typography>Segmentos</Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 10 }}>
                  {filteredSegments.map((segment) => (
                    <Button
                      key={segment}
                      variant={selectedSegments.includes(segment) ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => handleSegmentClick(segment)}
                      style={{ margin: '5px' }}>
                      {segment}
                    </Button>
                  ))}
                </div>
              </CardContent>

              <Divider style={{ margin: '10px 0' }} />

              <CardContent>
                <Typography>Orden</Typography>
                <RadioGroup
                  value={sortOrder}
                  onChange={handleSortChange}
                  style={{ display: 'flex' }}>
                  <FormControlLabel value="desc" control={<Radio />} label="Mayor a menor" />
                  <FormControlLabel value="asc" control={<Radio />} label="Menor a mayor" />
                </RadioGroup>
              </CardContent>

              <Divider style={{ margin: '10px 0' }} />

              <CardContent>
                <Button variant="outlined" color="secondary" onClick={handleClearFilters} fullWidth>
                  Limpiar filtros
                </Button>
              </CardContent>
            </Card>
            <Grid className={classes.cardGenerateOffer}>
              <Typography className={classes.textOffer}>
                Selecciona y envía las OFERTAS a tu cliente potencial.
              </Typography>
              <Button
                color="primary"
                variant="contained"
                className={classes.fullWidthButton}
                onClick={handlePreviewClick}
                disabled={selectedQuotes.length === 0}>
                Previsualizar Oferta
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}

      <QuotePreviewModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        selectedQuotes={selectedQuotes}
        installmentPayments={installmentPayments}
      />
    </div>
  );
};

export default QuoteResultClient;
